import type { FC, ReactNode } from 'react'

import Link from 'next/link'

import { contact, siteConfig } from '@/constants/site'

interface LinkItem {
    label: string
    href: string
}

const isExternal = (href: string): boolean => /^(https?:|mailto:|tel:)/.test(href)
const opensNewTab = (href: string): boolean => /^https?:/.test(href)

const PAGES: LinkItem[] = [
    { label: 'Home', href: '/' },
    { label: 'Client stories', href: '/showcases' },
    { label: 'Articles', href: '/insights' },
    { label: 'Coaching', href: '/what-we-do' },
    { label: 'Team', href: '/who-we-are' }
]

const LEGAL: LinkItem[] = [
    { label: 'Privacy', href: '/privacy' },
    { label: 'Contact', href: '/contact' }
]

const REACH: LinkItem[] = [
    { label: contact.officePhone, href: contact.officePhoneHref },
    { label: 'Free consultation', href: contact.consultationPhoneHref },
    { label: 'Eugene, Oregon', href: 'https://maps.google.com/?cid=1228871788174456380' }
]

const SOCIALS = [
    { label: 'Facebook', href: siteConfig.links?.facebook ?? '', icon: '/images/facebook.svg', n: '_4' }
]

const FooterLink: FC<LinkItem> = ({ label, href }): ReactNode => {
    const inner = (
        <>
            <div>{label}</div>
            <div className='link-block-underline' />
        </>
    )

    return (
        <div className='fade-in-move-on-scroll'>
            {isExternal(href) ? (
                <a
                    href={href}
                    target={opensNewTab(href) ? '_blank' : undefined}
                    rel={opensNewTab(href) ? 'noopener noreferrer' : undefined}
                    className='footer-link mdl-inline-block'
                >
                    {inner}
                </a>
            ) : (
                <Link href={href} className='footer-link mdl-inline-block'>
                    {inner}
                </Link>
            )}
        </div>
    )
}

export const Footer: FC = (): ReactNode => {
    return (
        <div className='footer'>
            <div className='cta'>
                <div className='container cta-container'>
                    <div className='cta-bg-top fade-in-on-scroll'>
                        <span className='cta-bg-image-top' aria-hidden='true' />
                    </div>
                    <div className='cta-grid fade-in-move-on-scroll'>
                        <div className='content'>
                            <h2 className='no-margin-top'>Ready to Stop Surviving and Start Winning?</h2>
                            <p className='grey-text'>
                                Let&apos;s talk. No pressure, just an honest conversation to see if we&apos;re the
                                right fit. Schedule your free orthodontic strategy session today.
                            </p>
                        </div>
                        <div className='content'>
                            <div className='cta-button-holder'>
                                <Link href='/contact' className='button mdl-button'>
                                    let&apos;s talk
                                </Link>
                            </div>
                        </div>
                    </div>
                    <div className='cta-bg-bottom fade-in-on-scroll'>
                        <span className='cta-bg-image-bottom' aria-hidden='true' />
                    </div>
                </div>
            </div>

            <div className='container container-footer'>
                <div className='footer-content'>
                    <div className='content'>
                        <div className='footer-brand-content fade-in-move-on-scroll'>
                            <div className='footer-title'>Providing Winning Strategies for Your Practice &amp; Your Life</div>
                            <a
                                href={`mailto:${contact.email}`}
                                className='footer-email-link mdl-inline-block'
                            >
                                <div className='footer-email-box' />
                                <div>{contact.email}</div>
                            </a>
                        </div>
                    </div>

                    <div className='footer-grid-content'>
                        <div className='footer-heading fade-in-move-on-scroll'>Pages</div>
                        {PAGES.map((item) => (
                            <FooterLink key={item.label} {...item} />
                        ))}
                    </div>

                    <div className='footer-grid-content'>
                        <div className='footer-heading fade-in-move-on-scroll'>Legal</div>
                        {LEGAL.map((item) => (
                            <FooterLink key={item.label} {...item} />
                        ))}
                    </div>

                    <div className='footer-grid-content'>
                        <div className='footer-heading fade-in-move-on-scroll'>Reach us</div>
                        {REACH.map((item) => (
                            <FooterLink key={item.label} {...item} />
                        ))}
                    </div>
                </div>

                <div className='follow-us-main-panel fade-in-move-on-scroll'>
                    <div className='follow-us-panel'>
                        <div className='follow-us-text-holder'>
                            <div>Follow Us</div>
                            <img src='/images/arrow-down-right.svg' loading='lazy' alt='' className='follow-us-arrow-image' />
                        </div>
                        <div className='social-media-icons'>
                            {SOCIALS.map((s) => (
                                <a
                                    key={s.label}
                                    href={s.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={`social-media-link ${s.n} mdl-inline-block`}
                                    aria-label={`Czechmate Consulting on ${s.label}`}
                                >
                                    <img src={s.icon} loading='lazy' alt='' className='social-media-icon' />
                                    <div className='link-background white-link-bg' />
                                </a>
                            ))}
                        </div>
                    </div>
                </div>

                <div className='made-by-section'>
                    <Link href='/' className='footer-logo-link' aria-label='Czechmate Consulting home'>
                        <img src='/images/czechmate/logo-full.png' alt='Czechmate Consulting' className='footer-image' width={120} height={85} />
                    </Link>
                    <div className='copyright-panel'>
                        <div className='copyright-text'>
                            © Czechmate Consulting, Eugene, Oregon. Your coach in the game of orthodontics.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
